import React, { useState, useMemo } from 'react';
import CalcShell, { StepsPanel } from '../CalcShell';

export default function RootCalc() {
  const [num, setNum] = useState(64);
  const [deg, setDeg] = useState(3);
  const [showWork, setShowWork] = useState(false);
  const val = useMemo(() => {
    if (num < 0 && deg % 2 === 0) return null;
    return num < 0 ? -Math.pow(-num, 1/deg) : Math.pow(num, 1/deg);
  }, [num, deg]);
  return (
    <CalcShell look="cnet" title="Root Calculator" subtitle="Compute the nth root of a number.">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div>
          <div className="field"><label>Number</label><input type="number" step="any" value={num} onChange={e => setNum(+e.target.value || 0)} data-testid="root-num"/></div>
          <div className="field"><label>Root degree (n)</label><input type="number" value={deg} onChange={e => setDeg(+e.target.value || 1)} data-testid="root-deg"/></div>
        </div>
        <div>
          <div className="result-panel"><div className="result-title">{deg}√{num}</div><div className="result-value" data-testid="root-result">{val === null ? 'Undefined (even root of negative)' : val.toFixed(8)}</div></div>
        </div>
      </div>
      <div className="mt-4">
        <button onClick={() => setShowWork(v => !v)} data-testid="root-show-work-toggle"
          className="text-sm px-4 py-1.5 rounded bg-slate-200 hover:bg-slate-300 text-slate-800 font-medium">
          {showWork ? '− Hide working' : '+ Show working'}
        </button>
        {showWork && (
          <div className="mt-4 p-5 bg-blue-50 border border-blue-100 rounded-xl" data-testid="root-work-panel">
            <h3 className="text-lg font-bold text-slate-900 mb-3">Step-by-step working</h3>
            <ol className="list-decimal ml-5 space-y-2 text-slate-800 text-[15px] leading-7">
              <li><b>Definition</b>: <span className="font-mono"><sup>n</sup>√x</span> is the number y such that <span className="font-mono">y<sup>n</sup> = x</span>.</li>
              <li><b>Rewrite as a fractional exponent</b>: <span className="font-mono"><sup>{deg}</sup>√{num} = {num}<sup>1/{deg}</sup></span></li>
              {num < 0 && deg % 2 === 0 && <li className="text-red-600"><b>Undefined</b> — cannot take an even root of a negative number in the real domain.</li>}
              {val !== null && (
                <>
                  <li><b>Compute</b>: <span className="font-mono">{num}<sup>1/{deg}</sup> = e<sup>(1/{deg}) × ln({Math.abs(num)})</sup> = e<sup>{(Math.log(Math.abs(num)) / deg).toFixed(4)}</sup> = {val.toFixed(8)}</span></li>
                  <li><b>Verify</b>: <span className="font-mono">{val.toFixed(4)}<sup>{deg}</sup> ≈ {Math.pow(val, deg).toFixed(4)} ≈ {num}</span></li>
                  <li><b>Final</b>: <b className="text-[#4c7b25]"><sup>{deg}</sup>√{num} = {val.toFixed(8)}</b></li>
                </>
              )}
            </ol>
          </div>
        )}
      </div>
    </CalcShell>
  );
}
